<template>
  <div>
    <v-container class="mb-0">
      <v-layout row wrap class="mb-0">
        <v-flex xs12>
          <v-layout row wrap align-center>
            <v-icon class="mr-3" @click="$router.go(-1)">arrow_back</v-icon>
            <h1 class="primary--text mr-3 d-inline" v-if="event">{{ event.title }}</h1>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

    <v-container grid-list-xl>
      <app-alert
        class="mt-4 mb-1"
        type="success"
        message="Event successfully created."
        :dismissible="true"
        v-if="$route.query.newEvent"
      />

      <!-- Event Information -->
      <v-layout row wrap>
        <v-flex xs12>
          <v-card>
            <v-card-title class="subheading font-weight-bold px-4">
              <span v-if="event">{{ event.title }}</span>
              <v-spacer></v-spacer>
              <v-btn
                small
                depressed
                color="accent"
                @click="editEvent = true"
                class="ma-0"
                v-if="event && canEditEvent"
              >
                <v-icon size="14" class="pr-1">edit</v-icon>Edit Event
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-container class="px-4" grid-list-xl>
              <v-layout row wrap v-if="event">
                <v-flex xs12 sm6 md4>
                  <b>Event Title</b>
                  <p>
                    {{ event.title }}
                  </p>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Event Description</b>
                  <br />
                  <span>{{ event.description }}</span>
                  <br />
                  <small class="grey--text">Affected Population: {{ event.affected.toLocaleString() }}</small>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Location</b>
                  <p>
                    {{ event.location.name }}, {{ event.location.tag }},
                    <router-link :to="`/events?s=${event.state.name}`">{{ event.state.name }}</router-link>
                  </p>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Date</b>
                  <p>{{ formatDate(event.timestamp) }}</p>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Reported By</b>
                  <p>{{ event.agency.name }}</p>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Status</b>
                  <p>
                    <v-chip
                      small
                      :color="event.status === 'ongoing' ? 'green' : 'red'"
                      text-color="white"
                      class="mx-0"
                    >{{ $lang(event.status) }}</v-chip>
                  </p>
                </v-flex>
              </v-layout>
              <v-layout row wrap justify-center class="pa-5" v-else>
                <v-progress-circular :indeterminate="true" :size="32" :width="3" v-if="isLoading"></v-progress-circular>
              </v-layout>
            </v-container>
          </v-card>
        </v-flex>
      </v-layout>

      <EditEvent
        v-if="event && canEditEvent"
        :isOpen="editEvent"
        :eventData="event"
        @closed="editEvent = false"
        @saved="updateEvent"
      />

      <!-- Beneficiaries -->
      <v-layout row wrap v-if="event">
        <v-flex xs12>
          <v-card>
            <v-card-title class="subheading font-weight-bold px-4">
              <span>Affected Population Breakdown</span>
              <v-spacer></v-spacer>
              <v-btn
                small
                depressed
                color="accent"
                @click.stop="addCategory = true"
                class="ma-0"
                v-if="canEditEvent"
              >
                <v-icon small>add</v-icon>Add Group
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-layout row wrap justify-center class="pa-5" v-if="isLoading">
              <v-progress-circular :indeterminate="true" :size="32" :width="3"></v-progress-circular>
            </v-layout>
            <v-data-table
              :headers="beneficiariesHeaders"
              :items="beneficiaries"
              :loading="isLoading"
              :hide-actions="beneficiaries.length < 5"
              no-data-text="No beneficiaries for this event"
              v-else
            >
              <template v-slot:items="props">
                <td>{{ (props.index + 1) }}</td>
                <td>{{ beneficiaryName(props.item.beneficiary_id) }}</td>
                <td>{{ props.item.value }}</td>
                <td>
                  <v-icon
                    small
                    @click="deleteRow(props.item._id, 'beneficiaries', 'event_beneficiary')"
                    v-if="canEditEvent"
                  >delete</v-icon>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>

      <AddPopulationGroup
        v-if="event && canEditEvent"
        :isOpen="addBeneficiary"
        :type="'event'"
        :typeId="event._id"
        :agent="currentUser._id"
        :beneficiaries="beneficiaries"
        @closed="addBeneficiary = false"
        @saved="updateBeneficiaries"
      />

      <!-- Interventions -->
      <v-layout row wrap v-if="event">
        <v-flex xs12>
          <v-card>
            <v-card-title class="subheading font-weight-bold px-4">
              <span>Interventions Recorded for {{ event.title }}</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-layout row wrap justify-center class="pa-5" v-if="isLoading">
              <v-progress-circular :indeterminate="true" :size="32" :width="3"></v-progress-circular>
            </v-layout>
            <v-data-table
              :headers="aidsHeaders"
              :items="aids"
              :loading="isLoading"
              :hide-actions="aids.length < 5"
              :no-data-text="'No Interventions recorded for ' + event.name"
              v-else
            >
              <template v-slot:items="props">
                <td>{{ (props.index + 1) }}</td>
                <td>{{ formatDate(props.item.timestamp) }}</td>
                <td>{{ formatDate(props.item.timestamp_end) }}</td>
                <td class="py-3">
                  <router-link :to="'/aids/' + props.item._id">
                    <b>{{ props.item.description }}</b>
                  </router-link>
                </td>
                <td>{{ formatCurrency(props.item.value) }}</td>
                <td>
                  <router-link :to="'/aids/' + props.item._id">
                    <v-icon small>visibility</v-icon>
                  </router-link>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { setFormData } from '@/utils/http'
import { FETCH_CATEGORY } from '@/store/actions/category'
import rolesData from '@/data/roles'

import AddPopulationGroup from '@/components/Modals/AddPopulationGroup.vue'
import EditEvent from './components/EditEvent.vue'

export default {
  name: 'Event',
  props: ['id'],
  components: {
    AddPopulationGroup,
    EditEvent
  },
	created () {
    this.isLoading = true
    this.isLoadingCategories = true

    // fetch all beneficiary categories in state
    this.$store.dispatch(FETCH_CATEGORY, { model: 'beneficiary' })
      .finally(() => (this.isLoadingCategories = false))
      
		// fetch this event and set related data
		const params = setFormData({ _id: this.id })
		this.$http.post('event/read_with_details', params)
			.then(({ data: response }) => {
				if (response.code === 200) {
          this.event = response.data
          if (!this.canViewEvent) {
            this.$router.push({ name: 'events' })
          }
          this.aids = this.event.aids || []
          this.beneficiaries = this.event.beneficiaries || []
				}
				else {
					this.$router.push({ name: 'events' })
				}
			})
			.finally(() => (this.isLoading = false))
	},
	data () {
		return {
      event: null,
      editEvent: false,
      
      beneficiaries: [],
      addBeneficiary: this.$route.query.newEvent || false,
			beneficiariesHeaders: [
        { text: '#', value: 'index', sortable: false },
				{ text: 'Category', value: 'beneficiary_id', sortable: false },
        { text: 'Value', value: 'value', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      aidsHeaders: [
        { text: '#', value: 'index', sortable: false },
        { text: 'Start Date', value: 'timestamp' },
        { text: 'End Date', value: 'timestamp' },
        { text: 'Description', value: 'description' },
        { text: 'Value', value: 'value' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      
      isLoading: false,
      isLoadingAgencies: false,
      isLoadingCategories: false
		}
	},
	computed: {
    canEditEvent () {
      return this.$can('edit_event') && 
      (this.currentUser._id === this.event.agent._id || rolesData.heads.includes(this.currentUser.role))
    },
    canViewEvent () {
      if (!this.$can('view_event')) return false
      else if (this.currentUser.role === rolesData.admin) return true
      else if (this.currentUser.parent === 'state' && this.currentUser.parent_id === this.event.state_id) return true
      else if (this.currentUser.parent === 'agency') return true
      return false
    },
    beneficiaryCategories () { // list fetched beneficiary categories
      return this.$store.getters.categories('beneficiary') || []
    },
    ...mapGetters(['currentUser'])
  },
  methods: {
		formatDate (timestamp) {
			return (new Date(timestamp * 1000)).toLocaleDateString(
				'en-US', { year: 'numeric', month: 'long', day: 'numeric' }
			)
    },
    formatCurrency (value) {
      const currency = new Intl.NumberFormat(
      'en-US', 
        { minimumFractionDigits: 2 }
      ).format(value)

      return '₦' + currency
    },
    updateEvent (payload) {
      this.event = Object.assign(this.event, payload)
    },
    beneficiaryName (id) {
      return this.$store.getters.categoryName('beneficiary', id)
    },
    updateBeneficiaries (payload) {
      this.beneficiaries.push(payload)
    },
    deleteRow (id, data, model) {
      const params = setFormData({ _id: id })
      this.$http.post(model + '/delete', params)
        .then(({ data: response }) => {
          if (response.code === 200) {
            this[data] = this[data].filter(item => item._id !== id)
          }
        })
    }
  }
}
</script>
